// packages/core/src/learning/schools/growth-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'cathie-wood'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const GROWTH_INVESTING_EXHIBIT: SchoolExhibitEntry = {
	id: 'growth-investing',
	tagline:
		'Growth investing believes the future can matter more than today’s price.',
	worldviewSummary:
		'Growth investing is paying attention to businesses that may become much larger than they look today. This lens protects you from only valuing what already exists on the income statement. It accepts the tradeoff that future potential is harder to judge than current profits. The blind spot is excitement: a great story, fast revenue, or popular company can still become a poor investment if the price assumes too much.',
	coreBeliefs: [
		'Growth investing believes some businesses are worth studying because they can reinvest capital for years.',
		'Growth investing believes the market can underestimate how large a company may become.',
		'Growth investing believes future earnings power matters more than today’s cheap looking price.',
		'Growth investing believes the best opportunities often look expensive before their full potential is obvious.',
		'Growth investing believes narrative is useful, but only when it is tied to real economics.',
		'Growth investing believes patience matters because the thesis usually needs time to prove itself.',
	],
	warnsAgainst: [
		'Ignoring future growth because a business looks expensive today.',
		'Confusing a low price with a good opportunity.',
		'Buying an exciting story without asking what expectations are already priced in.',
		'Treating fast growth as proof that risk has disappeared.',
	],
	helps: 'This lens helps people who want to understand how future earnings, reinvestment, markets, and business quality connect. It may not fit someone who wants simple rules, low attention investing, or a style where valuation risk feels too stressful.',
	figures: [],
	canon: [
		{
			title: 'Common Stocks and Uncommon Profits',
			author: 'Philip Fisher',
			oneLineIdea:
				'Look for businesses with room to grow, strong management, and advantages that can last.',
		},
		{
			title: 'The Innovator’s Dilemma',
			author: 'Clayton Christensen',
			oneLineIdea:
				'New markets can look small at first, then reshape entire industries when adoption compounds.',
		},
		{
			title: 'Total addressable market',
			oneLineIdea:
				'A growth lens asks how large the opportunity could become, not just what the company earns today.',
		},
		{
			title: 'Rule of 40',
			oneLineIdea:
				'Growth is stronger when revenue expansion and business discipline show up together.',
		},
		{
			title: 'Expectations investing',
			oneLineIdea:
				'The hard question is not whether the company is exciting, it is what future the price already assumes.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-growth-investing',
	debateIds: [],
	applyActions: [
		{
			label: 'Test how a high expectation plan changes your future range',
			action: {
				type: 'openSimulator',
				label: 'Simulate a growth case',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Compare your current money picture before taking more risk',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Write the difference between a company you like and a price that makes sense',
			action: {
				type: 'noToolAction',
				label: 'Write the growth thesis',
				prompt: 'Write what would need to become true for a growth story to justify its current price.',
			},
		},
		{
			label: 'See whether risk fits your runway before chasing upside',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
	],
	marketingHooks: [
		{
			line: 'Growth investing asks one dangerous question: what if today is not the real size of the business?',
			kind: 'hook',
		},
		{
			line: 'A popular company is not automatically a good investment. Price still matters.',
			kind: 'hook',
		},
		{
			line: 'What did you once think was growth, but later realized was just excitement?',
			kind: 'interview-question',
		},
		{
			line: 'If you had to explain one growth investment without naming the stock, what would the real thesis be?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users compare exciting money stories against real numbers, risk, and time.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Growth investing is not about worshiping the future. It is about asking what future the price already assumes.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience the difference between loving a company and understanding the expectations built into its price.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people whether they are buying growth, or buying a story they do not know how to test.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
